/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useCallback, useContext } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import { Link } from "@mui/material";

// Material Dashboard 2 React components

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import APIDataTable from "customComponents/Tables/APIDataTable";
import { useAlert } from "customComponents/Alert";

import { formatDate, isObjectEqual, formatTimeSpent } from "../../utils";

// Data

import PropTypes from "prop-types";
import AddDialog from "layouts/worklog/AddDialog";
import { getWorklogs, deleteWorklog, editWorklog } from "helper";
import DashboardNavbar from "customComponents/Navbars/DashboardNavbar";
import DashboardLayout from "customComponents/LayoutContainers/DashboardLayout";
import Footer from "customComponents/Footer";

import config from "../../config";

function WorklogData({ worklog }) {
  const [menu, setMenu] = useState(null);
  const [addDialog, setAddDialog] = useState(false);

  const alert = useAlert();

  const [worklogsData, setWorklogsData] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [filter, setFilter] = useState({});

  const columns = [
    { Header: "Project / Issue", accessor: "issue", width: "15%", align: "left" },
    { Header: "Employee", accessor: "employeeName", align: "center" },
    { Header: "Record time", accessor: "recordTime", align: "center" },
    { Header: "Time Spent", accessor: "timeSpent", align: "center" },
    { Header: "action", accessor: "action", align: "center" },
  ];

  const filterOptions = [
    { name: "from", label: "From", type: "date" },
    { name: "to", label: "To", type: "date" },
  ];

  useEffect(() => {
    fetchData();
  }, [page, limit, filter]);

  const fetchData = useCallback(async () => {
    try {
      let payload = { page, limit, sortBy: "worklogStarted:desc", ...filter };

      const res = await getWorklogs(payload);

      const { results, totalPages, totalResults } = res.data;

      const formattedData = formatData(results);

      setWorklogsData(formattedData);
      setTotalPages(totalPages);
      setTotalResults(totalResults);
    } catch (error) {
      console.log("error", error);

      const title = error.response ? error.response.statusText : "Unknown error";
      const message =
        error.response && error.response.data ? error.response.data.message : error.message;

      alert.show(title, message);
    }
  }, [page, limit, filter]);

  const editWorklogRequest = useCallback(
    async (Id, payload, onSuccess) => {
      try {
        await editWorklog(Id, payload);

        fetchData();

        onSuccess && onSuccess();
        alert.show("success", "Updated Successfully");
      } catch (error) {
        console.log("Edit Error", error);

        const title = error.response ? error.response.statusText : "Unknown error";
        const message =
          error.response && error.response.data ? error.response.data.message : error.message;

        alert.show(title, message);
      }
    },
    [page, limit, filter]
  );

  const deleteWorklogRequest = useCallback(
    async (Id, onSuccess) => {
      try {
        await deleteWorklog(Id);

        fetchData();

        onSuccess && onSuccess();
      } catch (error) {
        console.log("Delete Error", error);

        const title = error.response ? error.response.statusText : "Unknown error";
        const message =
          error.response && error.response.data ? error.response.data.message : error.message;

        alert.show(title, message);
      }
    },
    [page, limit, filter]
  );

  const formatData = (res) => {
    return res.map((value, index) => {
      const {
        worklogStarted,
        device,
        employee,
        issueSummary,
        projectName,
        issueIdOrKey,
        projectKey,
        timeSpentSeconds,
      } = value;

      return {
        issue: (
          <Link href={`${config.JIRA_API_URL}/browse/${issueIdOrKey}`} target="_blank">
            <MDBox
              display="flex"
              alignItems="left"
              flexDirection="column"
              lineHeight={1}
              sx={{ maxWidth: "240px" }}
            >
              {/* <MDBox lineHeight={1} ml={0}> */}
              <MDTypography
                variant="caption"
                fontWeight="medium"
                // color="text"
                noWrap={false}
                sx={{ wordBreak: "break-word" }}
              >
                {`${issueIdOrKey}:${issueSummary}`}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {projectName}
              </MDTypography>
              {/* </MDBox> */}
            </MDBox>
          </Link>
        ),
        employeeName: (
          <MDTypography variant="caption" fontWeight="medium">
            {employee?.name}
          </MDTypography>
        ),
        recordTime: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(worklogStarted)}
          </MDTypography>
        ),
        timeSpent: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatTimeSpent(timeSpentSeconds)}
          </MDTypography>
        ),
        action: (
          <MDTypography component="a" id={value.id} data={JSON.stringify(value)} onClick={openMenu}>
            <Icon>more_vert</Icon>
          </MDTypography>
        ),
      };
    });
  };

  const openMenu = ({ currentTarget }) => {
    setMenu(currentTarget);
  };

  const closeMenu = () => {
    setMenu(null);
  };

  const openAddDialog = (type = "add", worklogId = null, defaultValues = {}) => {
    setAddDialog({ type, worklogId, defaultValues });
  };

  const closeAddDialog = () => {
    setAddDialog(null);
  };

  const handleEdit = async () => {
    closeMenu();

    const dataJson = menu.getAttribute("data");
    console.log(dataJson);
    const data = JSON.parse(dataJson);

    const { employee, issueSummary, issueIdOrKey, worklogStarted, timeSpentSeconds } = data;
    // const projectNames = (projects ?? []).map((project) => project.name);

    openAddDialog("edit", menu.id, {
      issueSummary: `${issueIdOrKey}:${issueSummary}`,
      employeeName: employee.name,
      worklogStarted: worklogStarted,
      timeSpentSeconds,
    });
  };

  const handleDelete = () => {
    const worklogId = menu.id;
    console.log("worklogId", worklogId);
    closeMenu();

    alert.show(
      "Remove Employee Worklog",
      `Are you sure you want to delete worklog record?`,
      ["No", "Yes"],
      (index) => {
        if (index === 1) {
          deleteWorklogRequest(worklogId);
        }
      }
    );
  };

  const handleSubmit = (data) => {
    const { worklogId, type, ...rest } = data;

    if (type === "add") {
      console.log("data", data);
      // addWorklogRequest(rest, closeAddDialog); // edit is not allowed
    } else {
      if (rest.recordTime) {
        editWorklogRequest(worklogId, rest, closeAddDialog);
      } else {
        alert.show("Edit Worklog", "You must have at least one key to updete");
      }
    }
  };

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(menu)}
      onClose={closeMenu}
    >
      <MenuItem onClick={handleEdit}>Edit</MenuItem>
      <MenuItem onClick={handleDelete}>Delete</MenuItem>
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        <Card>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3} pb={0}>
            <MDBox>
              <MDTypography variant="h6">Worklogs</MDTypography>
            </MDBox>
          </MDBox>

          <MDBox>
            <APIDataTable
              table={{ rows: worklogsData, columns }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={false}
              canSearch
              currentPage={page}
              totalPages={totalPages}
              totalResults={totalResults}
              filterOptions={filterOptions}
              fetchData={(newPage, newLimit, newFilter) => {
                if (newPage !== page) {
                  setPage(newPage);
                }

                if (newLimit !== limit) {
                  setLimit(newLimit);
                }

                if (!isObjectEqual(newFilter, filter)) {
                  if (page !== 1 || newPage !== 1) {
                    setPage(1);
                  }

                  setFilter(newFilter);
                }
              }}
            />
          </MDBox>

          {renderMenu}
          {addDialog && (
            <AddDialog
              open={Boolean(addDialog)}
              {...addDialog}
              onClose={closeAddDialog}
              onSubmit={handleSubmit}
            />
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

WorklogData.defaultProps = {
  worklog: null,
};

// Typechecking props for the DataTable
WorklogData.propTypes = {
  worklog: PropTypes.object,
};

export default WorklogData;
